/**
 * SubAdminActivityLogModal — bir alt adminin (subadmin) VEYA şube yöneticisinin
 * (manager) işlem geçmişi (variant prop'u ile seçilir).
 *
 * Kaynak: SubAdminActivityLogs / ManagerActivityLogs (merkezi GTS DB) — kullanıcının
 * tüm yazma istekleri (POST/PUT/DELETE/PATCH) axios interceptor'ınca loglanır.
 * Bu modal sunucu taraflı sayfalama + tarih aralığı filtresiyle listeler.
 * Yalnız ana admin açabilir (liste uçları CompanyAdminOnly korumalı).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import {
  getSubAdminActivityList,
  getManagerActivityList,
  type SubAdminActivityLogItem,
} from '../../services/migration/personnelDataService';
import { describeActivity } from '../../services/activityLogDescriptions';
import { AppDatePicker } from '../common/AppDatePicker';

interface Props {
  open: boolean;
  onClose: () => void;
  uid: string | null;
  userName: string;
  /** Log kaynağı: subadmin → SubAdminActivityLogs, manager → ManagerActivityLogs. Varsayılan: subadmin. */
  variant?: 'subadmin' | 'manager';
}

/**
 * Eski kayıtlarda actionLabel ham path olabilir ya da hiç olmayabilir —
 * ekranda daima Türkçe açıklamaya çevrilir (describeActivity).
 */
function displayLabel(item: SubAdminActivityLogItem): string {
  const described = describeActivity(item.path, item.method);
  // describeActivity konu bulamazsa ham path döndürür; kayıtlı etiket varsa onu yeğle.
  if (described === item.path && item.actionLabel && item.actionLabel !== item.path) {
    return item.actionLabel;
  }
  return described;
}

const SubAdminActivityLogModal: React.FC<Props> = ({ open, onClose, uid, userName, variant = 'subadmin' }) => {
  const { t, i18n } = useTranslation();

  const [items, setItems] = useState<SubAdminActivityLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination 0 bazlı; backend 1 bazlı
  const [pageSize, setPageSize] = useState(25);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    setError(null);
    try {
      // Bitiş tarihi dahil olsun diye gün sonuna çekilir.
      const from = fromDate ? `${fromDate}T00:00:00Z` : undefined;
      const to = toDate ? `${toDate}T23:59:59Z` : undefined;
      const fetchList = variant === 'manager' ? getManagerActivityList : getSubAdminActivityList;
      const result = await fetchList(uid, page + 1, pageSize, from, to);
      setItems(result.items);
      setTotal(result.total);
    } catch (err: any) {
      console.error('[SubAdminActivityLogModal] Loglar yüklenemedi:', err);
      setError(err?.response?.data?.message || err?.message || t('users.activityLog.loadError'));
    } finally {
      setLoading(false);
    }
  }, [uid, page, pageSize, fromDate, toDate, variant, t]);

  useEffect(() => {
    if (open && uid) loadData();
  }, [open, uid, loadData]);

  // Modal her açılışta ilk sayfadan başlasın.
  useEffect(() => {
    if (open) setPage(0);
  }, [open, uid]);

  const formatDate = (item: SubAdminActivityLogItem) => {
    const date = item.actionEpochMs ? new Date(item.actionEpochMs) : new Date(item.actionAt);
    return date.toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const isSuccess = (statusCode: number | null) =>
    statusCode !== null && statusCode >= 200 && statusCode < 300;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
        <HistoryIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="div" noWrap>
            {t('users.activityLog.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {userName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {/* Filtreler */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <AppDatePicker
            label={t('users.activityLog.fromDate')}
            size="small"
            value={fromDate}
            onChange={(value) => {
              setFromDate(value);
              setPage(0);
            }}
          />
          <AppDatePicker
            label={t('users.activityLog.toDate')}
            size="small"
            value={toDate}
            onChange={(value) => {
              setToDate(value);
              setPage(0);
            }}
          />
          <Tooltip title={t('users.activityLog.refresh')}>
            <span>
              <IconButton onClick={loadData} disabled={loading} color="primary">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('users.activityLog.totalCount', { count: total })}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('users.activityLog.empty')}</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>{t('users.activityLog.columns.date')}</TableCell>
                  <TableCell>{t('users.activityLog.columns.action')}</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>
                    {t('users.activityLog.columns.status')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item)}</TableCell>
                    <TableCell>
                      {/* Teknik detay (API yolu/metodu) yalnız tooltip'te — tablo Türkçe kalır. */}
                      <Tooltip
                        title={`${item.method} ${item.apiName ? `[${item.apiName}] ` : ''}${item.path}`}
                      >
                        <Typography variant="body2" sx={{ maxWidth: 520 }} noWrap>
                          {displayLabel(item)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          isSuccess(item.statusCode)
                            ? t('users.activityLog.success')
                            : t('users.activityLog.failed')
                        }
                        color={isSuccess(item.statusCode) ? 'success' : 'error'}
                        variant={isSuccess(item.statusCode) ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage={t('users.activityLog.rowsPerPage')}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('common.close', { defaultValue: 'Kapat' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubAdminActivityLogModal;
